import { create } from 'zustand';
import { SimulationState } from '../types';
import type { SimulationMode, DemoSchool, IntegrityChecklist, SimulationActor } from '../types';
import { simulationApi } from '../services/simulationApi';

interface SimulationStoreState {
  state: SimulationState;
  schools: DemoSchool[];
  selectedSchool: DemoSchool | null;
  actors: SimulationActor[];
  selectedActors: string[];
  integrityChecklist: IntegrityChecklist | null;
  mode: SimulationMode;
  activeSessions: Record<string, any> | null;
  seedActive: boolean;
  seedLogs: string[];
  seedError: string | null;
  selectedScenarioId: string | null;
  
  loadSchools: () => Promise<void>;
  selectSchool: (school: DemoSchool | null) => Promise<void>;
  toggleActor: (actorId: string) => void;
  setMode: (mode: SimulationMode) => void;
  setSelectedScenarioId: (scenarioId: string | null) => void;
  runDemoSeed: (cleanManualSchools?: boolean) => Promise<void>;
  startSimulation: () => Promise<void>;
  stopSimulation: () => void;
}

export const useSimulationStore = create<SimulationStoreState>((set, get) => ({
  state: SimulationState.IDLE as SimulationState,
  schools: [],
  selectedSchool: null,
  actors: [],
  selectedActors: [],
  integrityChecklist: null,
  mode: 'MANUAL',
  activeSessions: null,
  seedActive: false,
  seedLogs: [],
  seedError: null,
  selectedScenarioId: null,

  loadSchools: async () => {
    set({ state: SimulationState.LOADING_SCHOOLS, seedError: null });
    try {
      const schools = await simulationApi.getDemoSchools();
      set({ schools, state: SimulationState.IDLE });
    } catch (err: any) {
      set({ state: SimulationState.FAILED, seedError: err?.message || 'Falha ao carregar escolas demo.' });
    }
  },

  selectSchool: async (school: DemoSchool | null) => {
    if (!school) {
      set({ selectedSchool: null, actors: [], selectedActors: [], integrityChecklist: null });
      return;
    }
    set({ selectedSchool: school, state: SimulationState.VALIDATING_SCHOOL, integrityChecklist: null });
    try {
      const integrity = await simulationApi.validateSchool(school.id);
      const actors = await simulationApi.getSchoolActors(school.id);

      // Fixos + equipe escola + amostra de PROF/RESP (cap 40 — prepareSession)
      const coreIds = actors
        .filter(
          (a) =>
            a.id.startsWith('00000001-') ||
            a.perfil === 'ESCOLA' ||
            a.perfil === 'DIRETOR' ||
            a.perfil === 'SECRETARIA' ||
            a.perfil === 'COORDENADOR',
        )
        .map((a) => a.id);
      const massSample = actors
        .filter(
          (a) =>
            (a.perfil === 'PROFESSOR' || a.perfil === 'RESPONSAVEL') &&
            !coreIds.includes(a.id),
        )
        .slice(0, 30)
        .map((a) => a.id);
      const merged = [...new Set([...coreIds, ...massSample])].slice(0, 40);
      const preSelected =
        merged.length > 0 ? merged : actors.slice(0, 5).map((a) => a.id);

      set({
        integrityChecklist: integrity,
        actors,
        selectedActors: preSelected,
        state: SimulationState.CONFIGURING,
      });
    } catch (err: any) {
      set({ state: SimulationState.FAILED, seedError: err?.message || 'Falha ao validar a escola.' });
    }
  },


  toggleActor: (actorId: string) => {
    const { selectedActors } = get();
    if (selectedActors.includes(actorId)) {
      set({ selectedActors: selectedActors.filter(id => id !== actorId) });
    } else {
      set({ selectedActors: [...selectedActors, actorId] });
    }
  },

  setMode: (mode: SimulationMode) => {
    set({ mode });
  },

  setSelectedScenarioId: (scenarioId: string | null) => {
    set({ selectedScenarioId: scenarioId });
  },

  runDemoSeed: async (cleanManualSchools = false) => {
    set({ seedActive: true, seedLogs: ['🔴 Iniciando processo de seed...'], seedError: null });
    
    // Inicia o polling dos logs em segundo plano
    let pollInterval: any = null;
    const pollLogs = async () => {
      try {
        const res = await simulationApi.getSeedLogs();
        if (res.logs && res.logs.length > 0) {
          set({ seedLogs: res.logs });
        }
        if (!res.active) {
          clearInterval(pollInterval);
        }
      } catch (err) {
        // Ignora erros de rede temporários
      }
    };
    pollInterval = setInterval(pollLogs, 800);

    try {
      const res = await simulationApi.executeDemoSeed(cleanManualSchools);
      // Executa um poll final
      await pollLogs();
      clearInterval(pollInterval);

      if (res.success) {
        set((state) => ({
          seedActive: false,
          seedLogs: [...state.seedLogs, '✅ Seed concluído com sucesso!', '🔄 Recarregando escolas demo...'],
        }));
        await get().loadSchools();
      } else {
        set((state) => ({
          seedActive: false,
          seedError: res.error || 'Falha no seed.',
          seedLogs: [...state.seedLogs, `❌ Erro: ${res.error}`],
        }));
      }
    } catch (err: any) {
      clearInterval(pollInterval);
      set((state) => ({
        seedActive: false,
        seedError: err?.message || 'Falha na execução do seed.',
        seedLogs: [...state.seedLogs, `❌ Erro fatal: ${err?.message || 'Conexão interrompida'}`],
      }));
    }
  },

  startSimulation: async () => {
    const { selectedSchool, selectedActors, mode, selectedScenarioId } = get();
    if (!selectedSchool) return;

    set({ state: SimulationState.PREPARING_SESSIONS });
    try {
      // 1. Prepara tokens de impersonação no backend
      let simulationSessionId: string | undefined;
      try {
        const { useSimSessionFocus } = await import('./useSimSessionFocus');
        simulationSessionId =
          useSimSessionFocus.getState().focusedSessionId || undefined;
      } catch {
        /* store opcional */
      }
      const res = await simulationApi.prepareSession(
        selectedSchool.id,
        selectedActors,
        simulationSessionId,
      );
      
      // Persiste as sessões no localStorage para uso dos iframes das janelas de simulação
      localStorage.setItem('ac_sessions', JSON.stringify(res.sessions));

      if (mode === 'AUTOMATIC' && selectedScenarioId) {
        set({ state: SimulationState.CONNECTING_DEVICES, activeSessions: res.sessions });
        // Dispara o cenário no backend
        await simulationApi.startScenario(selectedScenarioId, selectedSchool.id);
        set({ state: SimulationState.RUNNING });
      } else {
        // Modo manual
        set({
          activeSessions: res.sessions,
          state: SimulationState.MANUAL_CONTROL,
        });
      }
    } catch (err: any) {
      set({ state: SimulationState.FAILED, seedError: err?.message || 'Erro ao iniciar a simulação.' });
    }
  },

  stopSimulation: () => {
    const { mode } = get();
    if (mode === 'AUTOMATIC') {
      simulationApi.stopScenario().catch(console.warn);
    }
    // Limpa as sessões do localStorage
    localStorage.removeItem('ac_sessions');
    set({
      state: SimulationState.IDLE,
      activeSessions: null,
      selectedScenarioId: null,
    });
  },
}));
